import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, CheckCircle, XCircle, Clock } from "lucide-react";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";

interface AutomationRunHistoryProps {
  automationId: string;
}

export const AutomationRunHistory = ({ automationId }: AutomationRunHistoryProps) => {
  const [runs, setRuns] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRuns();
    
    // Subscribe to real-time updates
    const channel = supabase
      .channel(`automation-runs-${automationId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'automation_runs',
          filter: `automation_id=eq.${automationId}`
        },
        () => {
          fetchRuns();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [automationId]);

  const fetchRuns = async () => {
    try {
      const { data, error } = await supabase
        .from("automation_runs")
        .select("*")
        .eq("automation_id", automationId)
        .order("started_at", { ascending: false })
        .limit(20);

      if (error) throw error;
      setRuns(data || []);
    } catch (error: any) {
      toast.error("Failed to load run history");
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "success":
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case "failed":
        return <XCircle className="h-4 w-4 text-red-500" />;
      case "running":
        return <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />;
      default:
        return <Clock className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
      success: "default",
      failed: "destructive",
      running: "secondary",
      pending: "outline"
    };
    return <Badge variant={variants[status] || "outline"}>{status}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Run History</CardTitle>
      </CardHeader>
      <CardContent>
        {runs.length === 0 ? (
          <p className="text-muted-foreground text-center py-8">No runs yet</p>
        ) : (
          <div className="space-y-4">
            {runs.map((run) => (
              <div
                key={run.id}
                className="flex items-start gap-4 p-4 border border-border rounded-lg"
              >
                <div className="mt-1">{getStatusIcon(run.status)}</div>
                <div className="flex-1 space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="font-medium">
                      Run #{run.id.slice(0, 8)}
                    </div>
                    {getStatusBadge(run.status)}
                  </div>
                  {run.started_at && (
                    <div className="text-sm text-muted-foreground">
                      Started {formatDistanceToNow(new Date(run.started_at), { addSuffix: true })}
                    </div>
                  )}
                  {run.finished_at && (
                    <div className="text-sm text-muted-foreground">
                      Finished {formatDistanceToNow(new Date(run.finished_at), { addSuffix: true })}
                    </div>
                  )}
                  {run.error && (
                    <div className="text-sm text-destructive bg-destructive/10 p-2 rounded">
                      {run.error}
                    </div>
                  )}
                  {run.logs && Array.isArray(run.logs) && run.logs.length > 0 && (
                    <details className="text-sm">
                      <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                        View logs
                      </summary>
                      <div className="mt-2 space-y-1 bg-muted p-2 rounded font-mono text-xs">
                        {run.logs.map((log: any, i: number) => (
                          <div key={i}>{JSON.stringify(log)}</div>
                        ))}
                      </div>
                    </details>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
